/**
 * Flight-related API service functions
 */
import apiClient from './apiClient';
import { API_ENDPOINTS } from '../constants/api';

/** Coalesce parallel getFlightDetails calls (e.g. React Strict Mode double mount). */
const flightDetailsInflight = new Map();

export const flightService = {
  /**
   * Search for flights
   * @param {Object} searchParams - Flight search parameters
   * @returns {Promise} Flight search results
   */
  searchFlights: async (searchParams) => {
    return apiClient.post(API_ENDPOINTS.FLIGHTS.SEARCH, searchParams);
  },

  /**
   * Get flight details by ID
   * @param {string} flightId - Flight ID
   * @returns {Promise} Flight details
   */
  getFlightDetails: async (flightId, params = {}) => {
    const key = `${flightId}:${JSON.stringify(params)}`;
    if (flightDetailsInflight.has(key)) {
      return flightDetailsInflight.get(key);
    }

    const request = apiClient
      .get(`${API_ENDPOINTS.FLIGHTS.DETAILS}/${flightId}`, params)
      .finally(() => {
        setTimeout(() => flightDetailsInflight.delete(key), 300);
      });

    flightDetailsInflight.set(key, request);
    return request;
  },

  /**
   * Get multiple flight details in a single call (for return/multi-city flights)
   * @param {Object} params - { outbound_flight_id, return_flight_id } or { flight_ids: [] }
   * @returns {Promise} Multiple flight details
   */
  getMultipleFlightDetails: async (params) => {
    return apiClient.post(`${API_ENDPOINTS.FLIGHTS.MULTIPLE_FLIGHTS}`, params);
  },

  /**
   * Check flight availability
   * @param {string} flightId - Flight ID
   * @param {string} date - Flight date
   * @returns {Promise} Availability data
   */
  checkAvailability: async (flightId, date) => {
    return apiClient.get(`${API_ENDPOINTS.FLIGHTS.AVAILABILITY}/${flightId}`, { date });
  },

  /**
   * Get flight prices
   * @param {Object} priceParams - Price search parameters
   * @returns {Promise} Price data
   */
  getFlightPrices: async (priceParams) => {
    return apiClient.post(API_ENDPOINTS.FLIGHTS.PRICES, priceParams);
  },

  /**
   * Get multi-city flight options
   * @param {Array} segments - Flight segments
   * @returns {Promise} Multi-city flight options
   */
  getMultiCityFlights: async (segments) => {
    return apiClient.post(`${API_ENDPOINTS.FLIGHTS.SEARCH}/multi-city`, { segments });
  },

  /**
   * Get return flight options
   * @param {Object} returnParams - Return flight parameters
   * @returns {Promise} Return flight options
   */
  getReturnFlights: async (returnParams) => {
    return apiClient.post(`${API_ENDPOINTS.FLIGHTS.SEARCH}/return`, returnParams);
  }
};

export default flightService;